import Link from 'found/Link';
import PropTypes from 'prop-types';
import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useIntl } from 'react-intl';
import { createPaginationContainer, graphql } from 'react-relay';

import BadgeActive from 'src/components/Badge/BadgeActive';
import LoadMoreButton from 'src/components/LoadMoreButton';
import Loading from 'src/components/Loading';
import UUID from 'src/components/UUID';
import Breadcrumbs from 'src/enosikit/components/Breadcrumbs';
import { getCurrencySymbolByLocale } from 'src/util/i18n/currency';
import { getLocale } from 'src/util/i18n/handler';
import buildStandingChargeRate from 'src/util/i18n/tariff/standingCharge';
import logger from 'src/util/logger';
/**
 * Description
 * @param {object} props
 * @returns {React.ReactElement} - AdminStandingChargeList component
 */
function AdminStandingChargeList(props) {
  const loadMore = () => {
    const { relay } = props;

    if (!relay.hasMore() || relay.isLoading()) {
      return;
    }

    relay.loadMore(
      50,
      (error) => { logger.log(error); },
    );
  };

  if (!props) {
    return <Loading />;
  }

  const { relay, viewer } = props;
  const { standingCharges } = viewer;
  const intl = useIntl();
  const userLocale = getLocale();
  const currencySymbol = getCurrencySymbolByLocale(userLocale);

  // Page title and meta information
  const pageTitle = intl.formatMessage({
    id: 'admin.admin_standing_charge.admin_standing_charge_list.page.title',
    defaultMessage: 'Enosi Admin - Standing Charges',
  });

  // Breadcrumb labels
  const breadcrumbAdmin = intl.formatMessage({
    id: 'admin.admin_standing_charge.admin_standing_charge_list.breadcrumbs.admin.label',
    defaultMessage: 'Admin',
  });
  const breadcrumbStandingCharges = intl.formatMessage({
    id: 'admin.admin_standing_charge.admin_standing_charge_list.breadcrumbs.standing_charges.label',
    defaultMessage: 'Standing Charges',
  });

  // Table headers
  const tableHeaderTitle = intl.formatMessage({
    id: 'admin.admin_standing_charge.admin_standing_charge_list.table.header.title.label',
    defaultMessage: 'Title',
  });
  const tableHeaderIdentifier = intl.formatMessage({
    id: 'admin.admin_standing_charge.admin_standing_charge_list.table.header.identifier.label',
    defaultMessage: 'Identifier',
  });
  const tableHeaderId = intl.formatMessage({
    id: 'admin.admin_standing_charge.admin_standing_charge_list.table.header.id.label',
    defaultMessage: 'ID',
  });
  const tableHeaderExternalIdentifier = intl.formatMessage({
    id: 'admin.admin_standing_charge.admin_standing_charge_list.table.header.external_identifier.label',
    defaultMessage: 'External Identifier',
  });
  const tableHeaderExtId = intl.formatMessage({
    id: 'admin.admin_standing_charge.admin_standing_charge_list.table.header.external_identifier.short.label',
    defaultMessage: 'Ext ID',
  });
  const tableHeaderRate = intl.formatMessage({
    id: 'admin.admin_standing_charge.admin_standing_charge_list.table.header.rate.label',
    defaultMessage: 'Rate',
  });
  const tableHeaderTariff = intl.formatMessage({
    id: 'admin.admin_standing_charge.admin_standing_charge_list.table.header.tariff.label',
    defaultMessage: 'Tariff',
  });

  // Load more button states
  const copyLoadMore = intl.formatMessage({
    id: 'admin.admin_standing_charge.admin_standing_charge_list.load_more_button.state_load_more.label',
    defaultMessage: 'Load more...',
  });
  const copyNoMore = intl.formatMessage({
    id: 'admin.admin_standing_charge.admin_standing_charge_list.load_more_button.state_no_more.label',
    defaultMessage: 'No more standing charges to load',
  });

  return (
    <>
      <Helmet>
        <meta charSet="utf-8" />
        <title>{pageTitle}</title>
      </Helmet>

      <Breadcrumbs
        breadcrumbs={[
          { name: breadcrumbAdmin, path: '/admin' },
          { name: breadcrumbStandingCharges },
        ]}
      />

      <div className="clearfix">
        <h1 className="float-start">{breadcrumbStandingCharges}</h1>
      </div>

      <div className="mt-4 mb-4 card">
        <div className="table-responsive">
          <table className="table">
            <thead>
              <tr>
                <th scope="col">{tableHeaderTitle}</th>
                <th scope="col">{tableHeaderIdentifier}</th>
                <th scope="col">{tableHeaderId}</th>
                <th scope="col">
                  <abbr title={tableHeaderExternalIdentifier}>{tableHeaderExtId}</abbr>
                </th>
                <th scope="col">{tableHeaderRate}</th>
                <th scope="col">{tableHeaderTariff}</th>
              </tr>
            </thead>
            <tbody>
              {
                standingCharges && standingCharges.edges && standingCharges.edges.map((edge) => {
                  const { node } = edge;
                  const {
                    uuid, title, identifier, externalIdentifier, rate, timeUnit, tariff,
                  } = node;

                  return (
                    <tr key={`admin-standing-charge-${uuid}`}>
                      <th scope="row" colSpan={2}>
                        <Link to={`/admin/standing-charges/${uuid}`}>
                          {title}
                        </Link>
                        <br />
                        {identifier}
                      </th>
                      <td colSpan={2}>
                        <UUID uuid={uuid} />
                        <br />
                        {externalIdentifier}
                      </td>
                      <td>
                        {buildStandingChargeRate(intl, timeUnit, rate, currencySymbol)}
                      </td>
                      <td>
                        {tariff && (
                          <>
                            <Link to={`/admin/tariffs/${tariff.uuid}`}>
                              {tariff.title}
                            </Link>
                            <br />
                            <BadgeActive
                              start={tariff.active.start}
                              finish={tariff.active.finish}
                            />
                          </>
                        )}
                      </td>
                    </tr>
                  );
                })
              }
            </tbody>
          </table>
        </div>
        <div className="card-footer">
          <LoadMoreButton
            copyLoadMore={copyLoadMore}
            copyNoMore={copyNoMore}
            onClick={loadMore}
            hasNext={relay.hasMore()}
          />
        </div>
      </div>
    </>
  );
}

AdminStandingChargeList.propTypes = {
  relay: PropTypes.shape({
    hasMore: PropTypes.func,
    isLoading: PropTypes.func,
    loadMore: PropTypes.func,
  }).isRequired,
  viewer: PropTypes.object, // eslint-disable-line react/forbid-prop-types
};

AdminStandingChargeList.defaultProps = {
  viewer: null,
};

export default createPaginationContainer(
  AdminStandingChargeList,
  {
    viewer: graphql`
      fragment AdminStandingChargeList_viewer on Viewer {
        id
        viewerUser {
          email
          givenName
          familyName
        }
        standingCharges(
          first: $count
          after: $cursor
        ) @connection(key: "AdminStandingChargeList_standingCharges") {
          edges {
            cursor
            node {
              uuid
              externalIdentifier
              identifier
              title
              rate
              timeUnit
              tariff {
                uuid
                title
                active { start finish }
              }
            }
          }
          count
          pageInfo {
            startCursor
            endCursor
            hasNextPage
            hasPreviousPage
          }
        }
      }
    `,
  },
  {
    direction: 'forward',
    getConnectionFromProps(props) {
      return props.viewer.standingCharges;
    },
    // This is also the default implementation of `getFragmentVariables` if it isn't provided.
    getFragmentVariables(prevVars, totalCount) {
      return {
        ...prevVars,
        count: totalCount,
      };
    },
    getVariables(props, { count, cursor }) {
      return {
        count,
        cursor,
      };
    },
    query: graphql`
    # Pagination query to be fetched upon calling 'loadMore'.
    # Notice that we re-use our fragment, and the shape of this query matches our fragment spec.
    query AdminStandingChargeList_Pagination_Query(
      $count: Int!
      $cursor: String
    ) {
      viewer {
        ...AdminStandingChargeList_viewer
      }
    }
  `,
  },
);
